import { FC, useState } from "react";
import PageTemplate from "../../components/templates/PageTemplate";
import { Grid2, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Tabs from "../ProposalModule/Tabs";
import { RootState } from "../../app/store";
import { useSelector } from "react-redux";
import LoaderGlobal from "../../components/molecules/LoaderGlobal";
import NotFoundPage from "../../components/molecules/NotFoundPage";
import MenuRecipt from "../ProposalModule/MenuRecipt";
import ModalTemplate from "../../components/templates/ModalTemplate";
import { useCreateRegularSignatureMutation } from "../../app/api";
import { toast, ToastContainer } from "react-toastify";
import ContactCard from "../ProposalModule/ContactCard";
import SuccessPage from "../ProposalModule/SuccessPage";
import ContactUsModal from "../../components/organism/ContactUsModal";
import SignModal from "../ProposalModule/SignModal";
import PaymentModal from "../ProposalModule/PaymentModal";
import { getFontClass } from "../../helpers/fontUtils";
import Button from "../../components/atoms/Button";

interface Props {}

const Proposal: FC<Props> = () => {
  const [openSignModal, setOpenSignModal] = useState<boolean>(false);
  const [signature, setSignature] = useState<string>("");
  const [createSignature, { isSuccess }] = useCreateRegularSignatureMutation();
  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showPaymentErrorModal, setShowPaymentErrorModal] =
    useState<boolean>(false);
  const [onBehalfChecked, setOnBehalfChecked] = useState<boolean>(true);
  const { proposalSettings, loading, paymentLink } = useSelector(
    (state: RootState) => state.proposal,
  );
  const { locale, generalSettings } = useSelector(
    (state: RootState) => state.generalSettings,
  );

  const menuPrice = proposalSettings.proposal.menu.reduce(
    (acc, current) => (acc += current.price * (current.quantity ?? 0)),
    0,
  );

  const params = new URLSearchParams(window.location.search);
  const paymentStatus = params.get("payment");

  if (!generalSettings.booking_proposal_styling) {
    return (
      <PageTemplate>
        <LoaderGlobal />
      </PageTemplate>
    );
  }

  const handlePayment = () => {
    setShowPaymentModal(false);

    if (paymentLink && typeof paymentLink === "string") {
      window.location.href = paymentLink;
    }

    if (
      (typeof paymentLink === "boolean" && paymentLink === false) ||
      paymentLink === undefined
    ) {
      setShowPaymentErrorModal(true);
    }

    if (Array.isArray(paymentLink) && paymentLink.length > 0) {
      window.location.href = paymentLink[0];
    }

    toast.success("Betaling vellykket!");
  };

  const renderPaymentModal = () => {
    return (
      <PaymentModal
        open={showPaymentModal}
        handleClose={() => setShowPaymentModal(false)}
        onPayment={handlePayment}
        data={
          proposalSettings.proposal.payment
            ? proposalSettings.proposal.payment
            : null
        }
        totalPrice={menuPrice}
        fontClass={getFontClass(
          generalSettings.booking_proposal_styling.font_family,
        )}
        stylingType="booking_proposal_styling"
      />
    );
  };

  const renderFailedPaymentModal = () => (
    <ModalTemplate
      open={showPaymentErrorModal}
      handleClose={() => setShowPaymentErrorModal(false)}
      className={getFontClass(
        generalSettings.booking_proposal_styling.font_family,
      )}
    >
      <div className="flex flex-col gap-6 p-8  bg-white">
        <span className="text-2xl font-semibold ">
          {globalThis.translate.em.proposal.signature_page.failed_payment}
        </span>
        <span className="text-[18px] font-normal ">
          {
            globalThis.translate.em.proposal.signature_page
              .payment_link_undefined
          }
        </span>
        <Button
          onClick={() => {
            setShowPaymentErrorModal(false);
            setShowPaymentModal(true);
          }}
          className="w-full h-16 border-2 mt-8 bg-[#BF5934] border-[#BF5934] rounded text-white  font-semibold text-[18px]"
          stylingType="booking_proposal_styling"
        >
          {globalThis.translate.em.proposal.signature_page.retry_payment}
        </Button>
      </div>
      {renderPaymentModal()}
    </ModalTemplate>
  );

  const renderModal = () => {
    return (
      <ModalTemplate
        open={openSignModal}
        handleClose={handleCloseModal}
        className={getFontClass(
          generalSettings.booking_proposal_styling.font_family,
        )}
      >
        <div className="p-2 flex flex-row justify-between items-center bg-[#FAFAFA]">
          <span className="font-semibold text-2xl "></span>
          <IconButton onClick={handleCloseModal} className="h-8 w-8">
            <CloseIcon />
          </IconButton>
        </div>
        <div className="py-2 px-4">
          <div className="px-2 flex flex-row gap-4 items-center">
            <div>
              <svg
                width="36"
                height="35"
                viewBox="0 0 36 35"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M34.6668 18.5565C28.0002 24.4099 27.0468 15.2015 23.5835 15.8615C19.6668 16.6065 17.1668 24.9082 19.6668 24.9082C22.1668 24.9082 18.8335 14.9999 15.5002 18.4249C12.1668 21.8499 11.1002 26.3249 8.39183 23.0049C-4.49984 7.1882 6.3335 -4.41681 11.6052 3.26153C14.9418 8.12153 8.8335 25.7949 1.3335 34.1665M13.0002 32.4999H29.6668"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <input
              className=" text-[18px] font-normal h-full w-full outline-none italic text-[#777777] bg-inherit"
              placeholder="Underskriv eller tast inn"
              value={signature}
              onChange={(event) => setSignature(event.target.value)}
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={onBehalfChecked}
                onChange={(event) => setOnBehalfChecked(event.target.checked)}
              />
              <span>
                {globalThis.translate.em.proposal.signature_page.on_behalf}
              </span>
            </label>
          </div>
          <div className="border-b-2 border-b-[#90959E] mx-2 py-1"></div>
          <div className="px-2 py-4 font-normal text-[18px] ">
            {globalThis.translate.em.proposal.signature_page.confirm_text}
          </div>
        </div>
        <div className="bg-[#FAFAFA] flex gap-1 rounded">
          <div className="flex flex-row justify-end items-center w-full gap-2 px-4 py-4">
            <Button
              onClick={handleCloseModal}
              className="py-[6px] px-6 rounded  font-semibold text-[18px]"
              stylingType="booking_proposal_styling"
            >
              {globalThis.translate.em.proposal.signature_page.cancel}
            </Button>
            <Button
              onClick={handleSendSignature}
              className="bg-[#BF5934] py-[6px] px-6 rounded text-white  font-semibold text-[18px]"
              stylingType="booking_proposal_styling"
            >
              {proposalSettings.proposal.payment
                ? globalThis.translate.em.proposal.signature_page.pay
                : globalThis.translate.em.proposal.signature_page.sign_button}
            </Button>
          </div>
        </div>
      </ModalTemplate>
    );
  };

  const handleCloseModal = () => {
    setOpenSignModal(false);
  };

  const handleOpenModal = () => {
    setOpenSignModal(true);
  };

  const handleSendSignature = () => {
    if (signature.length > 0) {
      setOpenSignModal(false);

      if (proposalSettings.proposal.payment) {
        setShowPaymentModal(true);
      }
      return createSignature({
        signature,
        id: proposalSettings.proposal.id,
        unique_key:
          !!proposalSettings.proposal.payment &&
          !!proposalSettings.proposal.payment[0]
            ? proposalSettings.proposal.payment[0].unique_key
            : undefined,
        type: "regular",
        on_behalf:
          proposalSettings.proposal.company !== null &&
          proposalSettings.proposal.company !== undefined
            ? onBehalfChecked
            : false,
      });
    }

    return toast.error(
      globalThis.translate.em.proposal.signature_page.toast_error,
    );
  };

  if (
    loading ||
    globalThis.translate === undefined ||
    Object.keys(generalSettings).length === 0
  ) {
    return (
      <PageTemplate>
        <LoaderGlobal />
      </PageTemplate>
    );
  }

  if (!proposalSettings) {
    return <NotFoundPage />;
  }

  if (
    (!proposalSettings.proposal.payment && isSuccess) ||
    paymentStatus === "success"
  ) {
    return (
      <PageTemplate stylingType="booking_proposal_styling">
        <SuccessPage
          openContactModal={() => setContactModalOpen(true)}
          fontClass={getFontClass(
            generalSettings.booking_proposal_styling.font_family,
          )}
          stylingType="booking_proposal_styling"
        />
        <ContactUsModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          bookingId={proposalSettings.proposal.id}
          fontClass={getFontClass(
            generalSettings.booking_proposal_styling.font_family,
          )}
          stylingType="booking_proposal_styling"
        />
      </PageTemplate>
    );
  }

  const renderFailedPayment = () => {
    return (
      <PageTemplate stylingType="booking_proposal_styling">
        <div
          className={`flex flex-col gap-6 p-8 border-2 border-[#BF5934] bg-white ${getFontClass(generalSettings.booking_proposal_styling.font_family)}`}
        >
          <span className="text-2xl font-semibold ">
            {globalThis.translate.em.proposal.signature_page.failed_payment}
          </span>
          <span className="text-[18px] font-normal ">
            {
              globalThis.translate.em.proposal.signature_page
                .failed_payment_text
            }
          </span>
          <Button
            onClick={handleOpenModal}
            className="w-full h-16 border-2 mt-8 bg-[#BF5934] border-[#BF5934] rounded text-white  font-semibold text-[18px]"
            stylingType="booking_proposal_styling"
          >
            {globalThis.translate.em.proposal.signature_page.retry_payment}
          </Button>
        </div>
        {renderModal()}
      </PageTemplate>
    );
  };

  if (paymentStatus === "fail") {
    return renderFailedPayment();
  }

  return (
    <PageTemplate stylingType="booking_proposal_styling">
      <Grid2
        container
        spacing={4}
        justifyContent={"center"}
        className={`w-full ${getFontClass(generalSettings.booking_proposal_styling.font_family)}`}
      >
        <Grid2 size={{ md: 3, xs: 10 }}>
          <div className="flex flex-col gap-6 lg:px-0 sm:px-4">
            <ContactCard proposalSettings={proposalSettings} />
            <div>
              <div className="flex flex-row gap-2">
                <div className="flex flex-col w-full">
                  <label className=" text-[14px]" htmlFor="startdato">
                    {globalThis.translate.em.proposal.signature_page.date}
                  </label>
                  <input
                    className="bg-[#F3F3F0]  rounded-lg px-4 py-[6px]"
                    id="startdato"
                    disabled
                    value={proposalSettings.proposal.date}
                  />
                </div>
              </div>
            </div>
            <div className="flex lg:flex-row flex-col gap-2">
              <div className="flex flex-col lg:w-[48%] w-full">
                <label className=" text-[14px]" htmlFor="ankomst_tid">
                  {globalThis.translate.em.proposal.signature_page.arrival_time}
                </label>
                <input
                  className="bg-[#F3F3F0]  rounded-lg px-4 py-[6px]"
                  id="ankomst_tid"
                  disabled
                  value={proposalSettings.proposal.time_from}
                />
              </div>
              <div className="flex flex-col lg:w-[48%] w-full">
                <label className=" text-[14px]" htmlFor="ankomst_tid">
                  {globalThis.translate.em.proposal.signature_page.exit_time}
                </label>
                <input
                  className="bg-[#F3F3F0]  rounded-lg px-4 py-[6px]"
                  id="ankomst_tid"
                  disabled
                  value={proposalSettings.proposal.time_to}
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <label className=" text-[14px]" htmlFor="antall_gjester">
                {globalThis.translate.em.proposal.signature_page.pax}
              </label>
              <input
                className="bg-[#F3F3F0]  rounded-lg px-4 py-[6px]"
                id="antall_gjester"
                disabled
                value={proposalSettings.proposal.guests}
              />
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-2">
            <span className="font-semibold text-[18px] ">
              {
                globalThis.translate.em.proposal.signature_page
                  .something_question
              }
            </span>
            <span className="font-normal text-[14px] ">
              {globalThis.translate.em.proposal.signature_page.any_questions}
            </span>
            {/* <a href={`mailto:${proposalSettings.settings.restaurant.email}`}> */}
            <Button
              onClick={() => setContactModalOpen(true)}
              className="w-full h-16 border-2 mt-8 bg-white rounded text-[18px]"
              stylingType="booking_proposal_styling"
            >
              {globalThis.translate.em.proposal.signature_page.email_to}
            </Button>
            {/* </a> */}
          </div>
        </Grid2>
        <Grid2 size={{ md: 6, xs: 10 }}>
          <div className="w-full flex flex-col gap-12">
            <div className="bg-white p-6 rounded-2xl gap-6 flex flex-col">
              {proposalSettings.proposal.menu.length > 0 && (
                <div>
                  <span className="text-2xl font-semibold ">
                    {globalThis.translate.em.proposal.signature_page.menu}
                  </span>
                  <table className="w-full mt-2 px-8">
                    <thead>
                      <tr className="text-[18px] font-semibold border-b border-gray-300">
                        <th className="py-2 pr-4 text-left">
                          {globalThis.translate.em.proposal.signature_page.name}
                        </th>
                        <th className="py-2 pr-4 text-right">
                          {
                            globalThis.translate.em.proposal.signature_page
                              .quantity
                          }
                        </th>
                        <th className="py-2 pr-4 text-right">
                          {
                            globalThis.translate.em.proposal.signature_page
                              .price
                          }
                        </th>
                        <th className="py-2 text-right">
                          {
                            globalThis.translate.em.proposal.signature_page
                              .total
                          }
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposalSettings.proposal.menu.map((menuItem, index) => {
                        return (
                          <MenuRecipt
                            menu={menuItem}
                            locale={locale}
                            key={`menu_item_${index}`}
                          />
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="text-[20px] font-semibold border-t border-gray-300">
                        <td colSpan={3} className="py-2 pr-4">
                          {
                            globalThis.translate.em.proposal.signature_page
                              .total
                          }
                        </td>
                        <td className="py-2 text-right">{menuPrice},-</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <hr />
              <div className="font-semibold  text-[20px]">
                <span className="flex justify-end">
                  {globalThis.translate.em.proposal.signature_page.total_price}{" "}
                  {menuPrice},-{" "}
                </span>
                {proposalSettings.proposal.payment &&
                  proposalSettings.proposal.payment[0].payment_amount > 0 && (
                    <div className="flex flex-row justify-end text-[18px] font-normal gap-1">
                      <span>
                        {
                          globalThis.translate.em.proposal.signature_page
                            .deposit
                        }
                      </span>
                      <span>
                        {Number(
                          proposalSettings.proposal.payment[0].payment_amount /
                            100,
                        ).toFixed(2)}
                        ,-
                      </span>
                    </div>
                  )}
                {proposalSettings.proposal.payment &&
                  proposalSettings.proposal.payment[0].payment_amount / 100 <
                    menuPrice && (
                    <div className="flex flex-row justify-end text-[18px] font-normal gap-1">
                      <span>
                        {
                          globalThis.translate.em.proposal.signature_page
                            .remaining_amount
                        }
                      </span>
                      <span>
                        {Number(
                          menuPrice -
                            proposalSettings.proposal.payment[0]
                              .payment_amount /
                              100,
                        ).toFixed(2)}
                        ,-
                      </span>
                    </div>
                  )}
              </div>
            </div>

            <Tabs
              equipments={proposalSettings.proposal.equipments}
              menus={proposalSettings.proposal.menu}
              locale={locale}
              terms={proposalSettings.settings.terms_conditions}
            />
          </div>
        </Grid2>
        <Grid2 size={{ md: 3, xs: 10 }}>
          <div className="flex flex-col gap-4 lg:px-0 sm:px-4">
            <div className="bg-white p-4 rounded-2xl flex flex-col gap-12">
              <div className="px-2">
                <span className=" font-semibold text-2xl">
                  {globalThis.translate.em.proposal.signature_page.total}
                </span>
                <div className="lg:pl-8 xs:pl-0 flex flex-col gap-4">
                  <div className="flex flex-row justify-between text-[18px] font-normal ">
                    <span>
                      {" "}
                      {
                        globalThis.translate.em.proposal.signature_page
                          .price_for_dishes
                      }
                    </span>
                    <span>{menuPrice},-</span>
                  </div>

                  <div className="flex flex-row justify-between text-[20px] font-semibold ">
                    <span>
                      {globalThis.translate.em.proposal.signature_page.amount}
                    </span>
                    <span>{menuPrice},-</span>
                  </div>
                  {proposalSettings.proposal.payment &&
                    proposalSettings.proposal.payment[0].payment_amount > 0 && (
                      <div className="flex flex-row justify-between text-[18px] font-normal gap-1">
                        <span>
                          {
                            globalThis.translate.em.proposal.signature_page
                              .deposit
                          }
                        </span>
                        <span>
                          {Number(
                            proposalSettings.proposal.payment[0]
                              .payment_amount / 100,
                          ).toFixed(2)}
                          ,-
                        </span>
                      </div>
                    )}
                  {proposalSettings.proposal.payment &&
                    proposalSettings.proposal.payment[0].payment_amount / 100 <
                      menuPrice && (
                      <div className="flex flex-row justify-between text-[18px] font-normal gap-1">
                        <span>
                          {
                            globalThis.translate.em.proposal.signature_page
                              .remaining_amount
                          }
                        </span>
                        <span>
                          {Number(
                            menuPrice -
                              proposalSettings.proposal.payment[0]
                                .payment_amount /
                                100,
                          ).toFixed(2)}
                          ,-
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div className="bg-[#F0F6F7] p-4 rounded-2xl">
              <span className="text-2xl font-semibold ">
                {
                  globalThis.translate.em.proposal.signature_page
                    .continue_with_signing
                }
              </span>
              {/* <div className="flex flex-row justify-between text-[16px] font-semibold  ">
                <span>Depositum å betale</span>
                <span>5490,00 NOK</span>
              </div> */}
              {/* <button className="w-full h-16 border-2 mt-8 bg-white border-[#BF5934] rounded text-[#BF5934]  font-semibold text-[18px]">
                PREVIEW I PDF
              </button> */}

              <Button
                onClick={handleOpenModal}
                className="w-full h-16 border-2 mt-8 bg-[#BF5934] border-[#BF5934] rounded text-white  font-semibold text-[18px]"
                stylingType="booking_proposal_styling"
              >
                {proposalSettings.proposal.payment
                  ? globalThis.translate.em.proposal.signature_page.sign_and_pay
                  : globalThis.translate.em.proposal.signature_page.sing}
              </Button>
            </div>
          </div>
        </Grid2>
      </Grid2>

      <SignModal
        handleCloseModal={handleCloseModal}
        handleSendSignature={handleSendSignature}
        signature={signature}
        setSignature={setSignature}
        proposalSettings={proposalSettings}
        openSignModal={openSignModal}
        data={
          proposalSettings.proposal.payment
            ? proposalSettings.proposal.payment
            : null
        }
        fontClass={getFontClass(
          generalSettings.booking_proposal_styling.font_family,
        )}
        onBehalfChecked={onBehalfChecked}
        setOnBehalfChecked={setOnBehalfChecked}
        stylingType="booking_proposal_styling"
        company={proposalSettings.proposal.company}
      />
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        className={getFontClass(
          generalSettings.booking_proposal_styling.font_family,
        )}
      />
      <ContactUsModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        bookingId={proposalSettings.proposal.id}
        fontClass={getFontClass(
          generalSettings.booking_proposal_styling.font_family,
        )}
        stylingType="booking_proposal_styling"
      />
      {showPaymentModal && renderPaymentModal()}
      {showPaymentErrorModal && renderFailedPaymentModal()}
    </PageTemplate>
  );
};

export default Proposal;
